import { CropAndRenderImage } from '@apps/guest/packages/layout-engine/components/CropAndRenderImage/CropAndRenderImage';
import { animationTransition } from '@shared/utils/animationTransition';
import { Box, BoxProps } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import { HeaderPhotoSchemaByWebsiteLayout } from '@apps/guest/websiteHeader';
import { LazyScreen } from '@apps/guest/packages/layout-engine/components/CoverPhoto/CoverPhoto';
import { getHeaderPhotoAspectRatio, getImageRendition } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import globalWindow from '@shared/core/globals';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { addRendition } from '@shared/utils/photoRendition';

interface FullBleedImageProps {
  photoDetails: HeaderPhotoSchemaByWebsiteLayout['brannan']['fullWidth'] | HeaderPhotoSchemaByWebsiteLayout['brannan']['pageWidth'];
  variant: 'screen' | 'page';
  showPlaceholder?: boolean;
}

const FULL_BLEED_IMAGE_PROPERTIES: Record<'parent', BoxProps> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100vw',
    overflow: 'hidden',
    // All CSS units are converted to values before doing the operation.
    // -50vw: is 50% of the viewport width
    // +50%: is 50% of the *element's* width
    //
    // it's effectively the same as setting `left: -50%; transform: translateX(50%);`
    // but we need to calculate based off the viewport, not the element's width
    left: 'calc(-50vw + 50%)',
  },
};

const PAGE_WIDTH_IMAGE_PROPERTIES: Record<'parent', BoxProps> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    left: '0px',
  },
};

const ASPECT_RATIO = {
  screen: 21 / 9,
  page: 16 / 9,
};

const PageOrScreenWidthImage = ({ photoDetails, variant, showPlaceholder }: FullBleedImageProps) => {
  const photo = photoDetails.photos?.[0];

  const computeAspectRatio = useMemo(() => {
    return getHeaderPhotoAspectRatio(photo?.cropInfo, photo);
  }, [photo]);

  const properties = useMemo(() => {
    const lazyScreenHeight = Math.min((globalWindow.innerWidth ?? 0) / computeAspectRatio, 600);
    if (variant === 'page') {
      return {
        // LazyScreen properties
        lazyScreenProps: {
          width: '100%',
          height: lazyScreenHeight,
          isFullBleed: false,
          isChildEagerLoadRequire: true,
        },

        inlineEditorProps: {
          wrapperCSS: { width: '100%', left: '0px' },
        },

        imageContainerProps: PAGE_WIDTH_IMAGE_PROPERTIES.parent,
        imageCropperProps: {
          canvasWidth: '100%',
        },
      };
    }

    return {
      // LazyScreen properties
      lazyScreenProps: {
        width: globalWindow.innerWidth ?? 0,
        height: lazyScreenHeight,
        isFullBleed: true,
        isChildEagerLoadRequire: true,
      },

      inlineEditorProps: {
        wrapperCSS: { width: '100vw', left: 'calc(-50vw + 50%)' },
      },

      imageContainerProps: FULL_BLEED_IMAGE_PROPERTIES.parent,
      imageCropperProps: {
        canvasWidth: '100vw',
      },
    };
  }, [computeAspectRatio, variant]);

  if (!photo) {
    return photoDetails.presentationLayout && showPlaceholder ? (
      <Box
        {...properties.imageContainerProps}
        height={600}
        backgroundColor="lightgray"
      />
    ) : null;
  }

  const { cropInfo } = photo;

  return (
    <LazyScreen
      key={photo.id}
      src={photo.url}
      {...properties.lazyScreenProps}
    >
      {(isLoaded): JSX.Element => {
        if (!photo) {
          return <></>;
        }

        return (
          <>
            <Box
              visibility={isLoaded ? 'visible' : 'hidden'}
              opacity={isLoaded ? 1 : 0}
              transition={animationTransition('opacity')}
              {...properties.imageContainerProps}
            >
              {cropInfo ? (
                <CropAndRenderImage
                  imageUrl={getImageRendition(photo.url)}
                  cropInfo={{
                    x: cropInfo.x,
                    y: cropInfo.y,
                    height: cropInfo.height,
                    width: cropInfo.width,
                    unit: cropInfo.unit,
                  }}
                  {...properties.imageCropperProps}
                />
              ) : (
                <Box
                  key={photo.id}
                  width="100%"
                  height="auto"
                  aspectRatio={`${ASPECT_RATIO[variant]}`}
                  backgroundImage={backgroundImageForUrl(addRendition({ url: photo.url, renditionSize: 'medium' }))}
                  backgroundSize="cover"
                  backgroundRepeat="no-repeat"
                  backgroundPosition="center"
                />
              )}
            </Box>
          </>
        );
      }}
    </LazyScreen>
  );
};

export default PageOrScreenWidthImage;
